import React from 'react';
import { HelpCircle, RefreshCw, XCircle, Tag, CheckCircle } from 'lucide-react';
import WidgetInfoPopover from './WidgetInfoPopover';

const WIDGET_AGENTS = ['RAG Agent (Claim Intake Questionnaire)'];
const WIDGET_PROMPT = `You are a Claims Intake Assistant. Based on the Claim Intake
Questionnaire in your knowledge base and the current conversation transcript,
suggest the next 2-3 most relevant questions the agent should ask.

Consider:
- What information has already been captured in the conversation
- What critical details are still missing
- The suspected fraud type: {fraud_type_from_widget_1}
- Logical flow of the conversation

OUTPUT FORMAT (JSON):
{
  "suggested_questions": [
    {
      "category": "Authorization Assessment",
      "question": "Did you personally initiate or authorize this transaction?",
      "purpose": "Determines if transaction was authorized or coerced",
      "priority": "high",
      "relevance_reason": "..."
    }
  ]
}`;

const PRIORITY_STYLES = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  low: 'bg-green-50 text-green-700 border-green-200'
};

const QuestionSuggestions = ({ result, isLoading, error, onRefresh }) => {
  const notApplicable = result?.not_applicable === true;
  const questions = result?.data?.suggested_questions || [];
  const hasData = questions.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-md shadow-blue-900/5 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#000080]/5 to-[#000080]/10">
        <div className="flex items-center gap-2">
          <HelpCircle size={15} style={{ color: '#000080' }} />
          <h3 className="font-semibold text-gray-800 text-sm">Next Question Suggestions</h3>
          {isLoading && hasData && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#000080' }} title="Updating..." />}
        </div>
        <div className="flex items-center gap-1">
          <WidgetInfoPopover agents={WIDGET_AGENTS} prompt={WIDGET_PROMPT} />
          <button onClick={onRefresh} disabled={isLoading} className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40" title="Refresh suggestions">
            <RefreshCw size={14} className={isLoading && !hasData ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="p-4">
        {notApplicable && !isLoading && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            <CheckCircle size={22} className="text-gray-300" />
            <p className="text-xs mt-1 text-gray-500">No questions needed — not a fraud claim call</p>
          </div>
        )}

        {!hasData && !notApplicable && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            {isLoading
              ? <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#000080', borderTopColor: 'transparent' }} />
              : <HelpCircle size={22} className="opacity-30" />}
            <p className="text-xs mt-1">{isLoading ? 'Consulting knowledge base...' : 'Waiting for transcript context...'}</p>
          </div>
        )}

        {error && !hasData && (
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-xs text-red-600">
            <XCircle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}

        {hasData && (
          <>
            <div className="space-y-2.5">
              {questions.map((q, i) => (
                <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border" style={{ backgroundColor: 'rgba(0,0,128,0.07)', color: '#000080', borderColor: 'rgba(0,0,128,0.15)' }}>
                      <Tag size={10} />{q.category}
                    </span>
                    {q.priority && (
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${PRIORITY_STYLES[q.priority] || PRIORITY_STYLES.medium}`}>
                        {q.priority}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800 font-medium leading-snug mb-1">{q.question}</p>
                  {q.purpose && <p className="text-xs text-gray-500 leading-relaxed">{q.purpose}</p>}
                  {q.relevance_reason && <p className="text-xs mt-1 italic" style={{ color: '#000080' }}>{q.relevance_reason}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionSuggestions;
